import React, { ComponentProps, useCallback, useState } from 'react';
import { SwitchTransition } from 'react-transition-group';
import useToggle from 'react-use/lib/useToggle';
import styled from 'styled-components';
import { buildImageSource, buildImageSourceSet } from '../../utils/buildImageSource';
import { pluralize } from '../../utils/pluralize';
import { useTranslation } from '../../utils/useTranslation';
import { Drawer, DrawerContent, DrawerTitle } from '../feedback/Drawer';
import { Skeleton } from '../feedback/Skeleton';
import { IconAdd } from '../icon/IconAdd';
import { IconArrowLeft } from '../icon/IconArrowLeft';
import { IconClose } from '../icon/IconClose';
import { IconImage } from '../icon/IconImage';
import { FormControl } from '../input/FormControl';
import { FormControlLabel, LabelPlacement } from '../input/FormControlLabel';
import { IconButton } from '../input/IconButton';
import { Input } from '../input/Input';
import { Switch } from '../input/Switch';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Stack } from '../layout/Stack';
import { Image } from '../media/Image';
import { MediaCover } from '../media/MediaCover';
import { TransitionSlideUp } from '../transition/TransitionSlideUp';
import { Typography } from '../typography/Typography';
import type { CollectionUI } from './types';

const ListItem = styled(Flex).attrs((props) => ({
    ...props,
    borderRadius: 1,
    cursor: 'pointer',
    width: '100%',
    direction: 'row',
    spacing: 2,
    alignItems: 'center',
}))`
    &:hover {
        background-color: var(--background-secondary);
    }
`;

interface CollectionListItemProps extends ComponentProps<typeof ListItem> {
    collection: CollectionUI;
}

const CollectionListItem = (props: CollectionListItemProps) => {
    const { collection, ...other } = props;
    const preview = collection.previewItems[0];

    return (
        <ListItem p={1} role="button" {...other}>
            <MediaCover width={48} height={48} borderRadius={1}>
                {preview?.type === 'image' && (
                    <Image
                        src={buildImageSource(preview.media, 'grid')}
                        srcSet={buildImageSourceSet(preview.media, 'grid')}
                    />
                )}
                {preview?.type === 'video' && <Image src={preview.media[0].firstFrameUrl} />}
                {!preview && <IconImage fill="var(--color-primary)" />}
            </MediaCover>
            <Box>
                <Typography variant="body" weight="medium">
                    {collection.displayName}
                </Typography>
                <Typography variant="caption" color="secondary">
                    {pluralize(collection.numberOfPosts, '%d пост', '%d поста', '%d постов')}
                </Typography>
            </Box>
        </ListItem>
    );
};

const CollectionListItemSkeleton = () => (
    <ListItem p={1} role="button">
        <Flex direction="row" alignItems="center" spacing={2} width="100%">
            <Skeleton variant="rect" width={48} height={48} borderRadius={1} />
            <Flex flexGrow={1}>
                <Skeleton variant="text" width="100%" height={24} />
                <Skeleton variant="text" width="30%" height={16} />
            </Flex>
        </Flex>
    </ListItem>
);

enum BookmarkStep {
    ADD_TO_NEW_COLLECTION = 'ADD_TO_NEW_COLLECTION',
    ADD_TO_EXIST_COLLECTION = 'ADD_TO_EXIST_COLLECTION',
    CLOSED = 'CLOSED',
}

interface BookmarksDialogProps {
    collections: CollectionUI[];
    isCollectionsLoading: boolean;
    onSaveToExistentCollection: (collectionId: string) => void;
    onSaveToNewCollection: (collectionName: string, isPublic: boolean) => void;
    onClose: VoidFunction;
}

export const SaveToCollectionDrawer = (props: BookmarksDialogProps) => {
    const {
        collections,
        isCollectionsLoading,
        onClose,
        onSaveToNewCollection,
        onSaveToExistentCollection,
    } = props;
    const [step, setStep] = useState<BookmarkStep>(BookmarkStep.ADD_TO_EXIST_COLLECTION);
    const [collectionName, setCollectionName] = useState('');
    const [isPublic, setIsPublic] = useToggle(false);
    const t = useTranslation();

    const onCloseCallback = useCallback(() => {
        setStep(BookmarkStep.CLOSED);
        onClose();
    }, [onClose, setStep]);

    const onSaveToNewCollectionCallback = useCallback(() => {
        setStep(BookmarkStep.CLOSED);
        onSaveToNewCollection(collectionName, isPublic);
    }, [onSaveToNewCollection, setStep, collectionName, isPublic]);

    const onSaveToExistentCollectionCallback = useCallback(
        (collectionId: string) => {
            setStep(BookmarkStep.CLOSED);
            onSaveToExistentCollection(collectionId);
        },
        [onSaveToExistentCollection, setStep],
    );

    return (
        <SwitchTransition>
            <TransitionSlideUp key={step}>
                <Drawer onClose={onCloseCallback}>
                    {step === BookmarkStep.ADD_TO_EXIST_COLLECTION && (
                        <>
                            <DrawerTitle>
                                <Typography variant="body" weight="bold" mx="auto">
                                    {t('Пост сохранен')}
                                </Typography>
                                <Box
                                    position="absolute"
                                    right={1}
                                    top="50%"
                                    transform="translateY(-50%)"
                                >
                                    <IconButton onClick={onCloseCallback}>
                                        <IconClose />
                                    </IconButton>
                                </Box>
                            </DrawerTitle>
                            <DrawerContent p={1} minHeight={500}>
                                <ListItem
                                    p={1}
                                    role="button"
                                    onClick={() => setStep(BookmarkStep.ADD_TO_NEW_COLLECTION)}
                                >
                                    <Box
                                        p={1.5}
                                        borderRadius={1}
                                        backgroundColor="var(--background-secondary)"
                                    >
                                        <IconAdd color="var(--color-primary)" fontSize={24} />
                                    </Box>
                                    <Typography variant="body">
                                        {t('Добавить в новую коллекцию')}
                                    </Typography>
                                </ListItem>
                                <Stack
                                    count={isCollectionsLoading ? 5 : collections.length}
                                    width="100%"
                                    spacing={2}
                                    renderItem={(index) => {
                                        const collection = collections[index];

                                        if (!collection) {
                                            return <CollectionListItemSkeleton key={index} />;
                                        }

                                        return (
                                            <CollectionListItem
                                                key={collection.id}
                                                collection={collection}
                                                onClick={() =>
                                                    onSaveToExistentCollectionCallback(
                                                        collection.id,
                                                    )
                                                }
                                            />
                                        );
                                    }}
                                />
                            </DrawerContent>
                        </>
                    )}

                    {step === BookmarkStep.ADD_TO_NEW_COLLECTION && (
                        <>
                            <DrawerTitle>
                                <Box
                                    position="absolute"
                                    left={1}
                                    top="50%"
                                    transform="translateY(-50%)"
                                >
                                    <IconButton
                                        onClick={() =>
                                            setStep(BookmarkStep.ADD_TO_EXIST_COLLECTION)
                                        }
                                    >
                                        <IconArrowLeft />
                                    </IconButton>
                                </Box>
                                <Typography variant="body" weight="bold" mx="auto">
                                    {t('Новая коллекция')}
                                </Typography>

                                <Box
                                    position="absolute"
                                    right={1}
                                    top="50%"
                                    transform="translateY(-50%)"
                                >
                                    <IconButton
                                        disabled={!collectionName}
                                        onClick={() => onSaveToNewCollectionCallback()}
                                    >
                                        <Typography variant="caption" weight="bold">
                                            Создать
                                        </Typography>
                                    </IconButton>
                                </Box>
                            </DrawerTitle>
                            <DrawerContent p={2}>
                                <FormControl width="100%" mb={2} label={t('Название коллекции')}>
                                    <Input
                                        autoFocus
                                        value={collectionName}
                                        type="text"
                                        autoComplete="off"
                                        maxLength={25}
                                        onChange={(e) => setCollectionName(e.currentTarget.value)}
                                    />
                                </FormControl>
                                <FormControlLabel
                                    label={
                                        <>
                                            <Typography>{t('Публичная коллекция')}</Typography>
                                            <Typography variant="caption" color="secondary">
                                                {t(
                                                    'Чтобы коллекцией можно было поделиться, ее нужно сделать публичной. Мы быстро проверим заголовок, и функция «Поделиться» станет доступна.',
                                                )}
                                            </Typography>
                                        </>
                                    }
                                    control={
                                        <Switch
                                            checked={isPublic}
                                            onChange={() => {
                                                setIsPublic();
                                            }}
                                        />
                                    }
                                    labelPlacement={LabelPlacement.LEFT}
                                    labelWidth={{ xs: '80%', sm: '90%' }}
                                />
                            </DrawerContent>
                        </>
                    )}
                </Drawer>
            </TransitionSlideUp>
        </SwitchTransition>
    );
};
